import React, { useState, useMemo } from 'react';
import {
  ChevronLeft,
  Plus,
  Search,
  Calendar,
  Trash2,
  Printer,
  ChevronDown,
  X,
  FileSpreadsheet,
  Check,
  RotateCcw,
  MoreVertical,
  AlertTriangle,
  Upload,
  QrCode,
  Download,
  Image as ImageIcon,
  CheckCircle2,
  Clock,
  Box,
} from 'lucide-react';

export type ParcelStatus = 'uncollected' | 'collected' | 'deleted' | 'returned';

export interface ParcelRecord {
  id: string;
  parcelNo: string; // e.g. 2403-21
  arrivedDate: string; // e.g. 15/03/2024 - 16:22 น.
  arrivedRawDate: string; // YYYY-MM-DD
  trackingNo: string;
  status: ParcelStatus;
  unitNo: string; // e.g. 1086/20
  recipientName: string; // e.g. นะ ศรีสุระ
  receiverName?: string; // e.g. เทส (Silverman support)
  carrier: string; // e.g. J&T Express
  parcelType: string; // e.g. ห่อใหญ่
  hasImage?: boolean;
  imageUrl?: string;
  issuer: string; // e.g. Silverman support
  reminderRound?: string; // e.g. รอบที่ : 1 12/03/2024
  collectedDate?: string; // e.g. 29/03/2024 - 12:57 น.
  deletedDate?: string; // e.g. 29/03/2024
  deletedBy?: string; // e.g. Silverman support
  deleteReason?: string; // e.g. เทส, ลบ
  returnedDate?: string; // e.g. 18/02/2022
  returnReason?: string; // e.g. ชื่อผิด, ส่งผิด, ลูกค้าไม่มารับ เกิน 7 วัน
}

export interface NewParcelRow {
  id: string;
  unitNo: string;
  recipientName: string;
  trackingNo: string;
  carrier: string;
  parcelType: string;
  imageUrl?: string;
}

const INITIAL_PARCELS: ParcelRecord[] = [
  {
    id: 'p-1',
    parcelNo: '2403-21',
    arrivedDate: '15/03/2024 - 16:22 น.',
    arrivedRawDate: '2024-03-15',
    trackingNo: '123123786',
    status: 'uncollected',
    unitNo: '1086/20',
    recipientName: 'นะ ศรีสุระ',
    receiverName: '-',
    carrier: 'J&T Express',
    parcelType: 'ห่อใหญ่',
    issuer: 'Silverman support',
    reminderRound: '-',
  },
  {
    id: 'p-2',
    parcelNo: '2403-20',
    arrivedDate: '15/03/2024 - 15:59 น.',
    arrivedRawDate: '2024-03-15',
    trackingNo: '123',
    status: 'uncollected',
    unitNo: '1086/20',
    recipientName: 'naretapple1 na',
    receiverName: '-',
    carrier: 'Flash Express',
    parcelType: 'ซองขนาดเล็ก',
    hasImage: true,
    issuer: 'Silverman support',
    reminderRound: '-',
  },
  {
    id: 'p-3',
    parcelNo: '2403-19',
    arrivedDate: '12/03/2024 - 11:07 น.',
    arrivedRawDate: '2024-03-12',
    trackingNo: '123123',
    status: 'uncollected',
    unitNo: '1086/01',
    recipientName: 'adisorn kaeophakdee',
    receiverName: '-',
    carrier: 'ไปรษณีย์ไทย',
    parcelType: 'ห่อกลาง',
    issuer: 'Silverman support',
    reminderRound: 'รอบที่ : 1 12/03/2024',
  },
  {
    id: 'p-4',
    parcelNo: '2403-18',
    arrivedDate: '08/03/2024 - 09:46 น.',
    arrivedRawDate: '2024-03-08',
    trackingNo: '123123',
    status: 'uncollected',
    unitNo: '1086/12',
    recipientName: 'adisorn kaeophakdee',
    receiverName: '-',
    carrier: 'ไปรษณีย์ไทย',
    parcelType: 'ห่อเล็ก',
    issuer: 'Silverman support',
    reminderRound: 'รอบที่ : 1 08/03/2024',
  },
  {
    id: 'p-5',
    parcelNo: '2403-17',
    arrivedDate: '07/03/2024 - 09:30 น.',
    arrivedRawDate: '2024-03-07',
    trackingNo: '123123',
    status: 'uncollected',
    unitNo: '1086/01',
    recipientName: 'adisorn kaeophakdee',
    receiverName: '-',
    carrier: 'ไปรษณีย์ไทย',
    parcelType: 'ห่อเล็ก',
    issuer: 'Silverman support',
    reminderRound: 'รอบที่ : 1 07/03/2024',
  },
  {
    id: 'p-6',
    parcelNo: '2403-16',
    arrivedDate: '07/03/2024 - 09:29 น.',
    arrivedRawDate: '2024-03-07',
    trackingNo: '123123',
    status: 'uncollected',
    unitNo: '1086/01',
    recipientName: 'adisorn kaeophakdee',
    receiverName: '-',
    carrier: 'ไปรษณีย์ไทย',
    parcelType: 'ห่อเล็ก',
    issuer: 'Silverman support',
    reminderRound: 'รอบที่ : 1 12/03/2024',
  },
  {
    id: 'p-7',
    parcelNo: '2403-15',
    arrivedDate: '07/03/2024 - 09:24 น.',
    arrivedRawDate: '2024-03-07',
    trackingNo: '123',
    status: 'uncollected',
    unitNo: '1086/01',
    recipientName: 'adisorn kaeophakdee',
    receiverName: '-',
    carrier: 'ไปรษณีย์ไทย',
    parcelType: 'ห่อเล็ก',
    issuer: 'Silverman support',
    reminderRound: 'รอบที่ : 1 12/03/2024',
  },
  // Collected
  {
    id: 'p-8',
    parcelNo: '2402-4',
    arrivedDate: '08/02/2024 - 18:09 น.',
    arrivedRawDate: '2024-02-08',
    collectedDate: '29/03/2024 - 12:57 น.',
    trackingNo: 'sdksfjsfkj8438',
    status: 'collected',
    unitNo: '22/22',
    recipientName: 'meaw meawmeaw',
    receiverName: 'meaw meawmeaw (Silverman support)',
    carrier: 'DHL',
    parcelType: 'ซองจดหมาย/เอกสาร',
    issuer: 'Silverman support',
  },
  {
    id: 'p-9',
    parcelNo: '2402-6',
    arrivedDate: '13/02/2024 - 23:06 น.',
    arrivedRawDate: '2024-02-13',
    collectedDate: '15/02/2024 - 10:15 น.',
    trackingNo: '45676456',
    status: 'collected',
    unitNo: '11/12',
    recipientName: '3fsf fsff',
    receiverName: '3fsf fsff',
    carrier: 'Kerry Express',
    parcelType: 'ซองจดหมาย/เอกสาร',
    issuer: 'Silverman support',
  },
  // Deleted
  {
    id: 'p-10',
    parcelNo: '2310-11',
    arrivedDate: '09/10/2023 - 13:40 น.',
    arrivedRawDate: '2023-10-09',
    deletedDate: '18/01/2024',
    trackingNo: '3574576342',
    status: 'deleted',
    unitNo: '1086/01',
    recipientName: 'jordan andeson',
    carrier: 'Ninja Van',
    parcelType: 'ซองขนาดเล็ก',
    deletedBy: 'adisorn test2024 dev',
    deleteReason: '-',
    issuer: 'Silverman support',
  },
  {
    id: 'p-11',
    parcelNo: '2310-29',
    arrivedDate: '18/10/2023 - 17:02 น.',
    arrivedRawDate: '2023-10-18',
    deletedDate: '17/01/2024',
    trackingNo: '441546',
    status: 'deleted',
    unitNo: '1086/01',
    recipientName: 'jordan andeson',
    carrier: 'Flash Express',
    parcelType: 'ห่อใหญ่',
    deletedBy: 'adisorn test2024 dev',
    deleteReason: '-',
    issuer: 'Silverman support',
  },
  {
    id: 'p-12',
    parcelNo: '2310-27',
    arrivedDate: '18/10/2023 - 15:01 น.',
    arrivedRawDate: '2023-10-18',
    deletedDate: '18/10/2023',
    trackingNo: '12345789',
    status: 'deleted',
    unitNo: '1086/20',
    recipientName: 'tobb tobsa',
    carrier: 'ไปรษณีย์ไทย',
    parcelType: 'ห่อกลาง',
    hasImage: true,
    deletedBy: 'Silverman support',
    deleteReason: 'sdf',
    issuer: 'Silverman support',
  },
  {
    id: 'p-13',
    parcelNo: '2309-34',
    arrivedDate: '26/09/2023 - 09:46 น.',
    arrivedRawDate: '2023-09-26',
    deletedDate: '26/09/2023',
    trackingNo: '123456',
    status: 'deleted',
    unitNo: '1/3',
    recipientName: 'สวยใส แซ่บซ่า',
    carrier: 'Shopee',
    parcelType: 'ห่อเล็ก',
    deletedBy: 'Silverman support',
    deleteReason: 'ลบ',
    issuer: 'Silverman support',
  },
  // Returned
  {
    id: 'p-14',
    parcelNo: '2202-150',
    arrivedDate: '18/02/2022 - 14:31 น.',
    arrivedRawDate: '2022-02-18',
    returnedDate: '18/02/2022',
    trackingNo: '8851753098736',
    status: 'returned',
    unitNo: '1086/02',
    recipientName: 'พัชราภา',
    carrier: 'Ninja Van',
    parcelType: 'กล่องขนาดกลาง',
    hasImage: true,
    returnReason: 'ชื่อผิด',
    issuer: 'Silverman support',
  },
  {
    id: 'p-15',
    parcelNo: '2202-141',
    arrivedDate: '18/02/2022 - 14:26 น.',
    arrivedRawDate: '2022-02-18',
    returnedDate: '18/02/2022',
    trackingNo: '8851753098736',
    status: 'returned',
    unitNo: '1086/01',
    recipientName: 'ดั๊ก ดำดี้',
    carrier: 'DHL',
    parcelType: 'กล่องขนาดเล็ก',
    hasImage: true,
    returnReason: 'ะพ้ะ',
    issuer: 'Silverman support',
  },
  {
    id: 'p-16',
    parcelNo: '2202-134',
    arrivedDate: '18/02/2022 - 14:24 น.',
    arrivedRawDate: '2022-02-18',
    returnedDate: '18/02/2022',
    trackingNo: '85896556',
    status: 'returned',
    unitNo: '1086/02',
    recipientName: 'ttt',
    carrier: 'ไปรษณีย์ไทย',
    parcelType: 'ซองขนาดเล็ก',
    returnReason: '225533',
    issuer: 'Silverman support',
  },
  {
    id: 'p-17',
    parcelNo: '2202-139',
    arrivedDate: '18/02/2022 - 14:25 น.',
    arrivedRawDate: '2022-02-18',
    returnedDate: '18/02/2022',
    trackingNo: '256114555',
    status: 'returned',
    unitNo: '1086/04',
    recipientName: 'วรัญญา',
    carrier: 'Shopee',
    parcelType: 'ซองขนาดใหญ่',
    returnReason: 'ส่งผิด',
    issuer: 'Silverman support',
  },
  {
    id: 'p-18',
    parcelNo: '2202-114',
    arrivedDate: '15/02/2022 - 14:09 น.',
    arrivedRawDate: '2022-02-15',
    returnedDate: '15/02/2022',
    trackingNo: '741252',
    status: 'returned',
    unitNo: '1086/12',
    recipientName: 'kkk',
    carrier: 'Kerry Express',
    parcelType: 'ซองขนาดเล็ก',
    returnReason: 'ส่งผิด',
    issuer: 'Silverman support',
  },
  {
    id: 'p-19',
    parcelNo: '2202-115',
    arrivedDate: '15/02/2022 - 14:09 น.',
    arrivedRawDate: '2022-02-15',
    returnedDate: '15/02/2022',
    trackingNo: '846',
    status: 'returned',
    unitNo: '1086/12',
    recipientName: 'kkk',
    carrier: 'DHL',
    parcelType: 'กล่องขนาดใหญ่',
    returnReason: '0',
    issuer: 'Silverman support',
  },
  {
    id: 'p-20',
    parcelNo: '2202-80',
    arrivedDate: '14/02/2022 - 14:00 น.',
    arrivedRawDate: '2022-02-14',
    returnedDate: '14/02/2022',
    trackingNo: 'th123456',
    status: 'returned',
    unitNo: '1086/08',
    recipientName: 'Rose',
    carrier: 'ไปรษณีย์ไทย',
    parcelType: 'กล่องขนาดเล็ก',
    returnReason: 'ไม่มีผู้รับ',
    issuer: 'Silverman support',
  },
  {
    id: 'p-21',
    parcelNo: '2202-2',
    arrivedDate: '01/02/2022 - 16:34 น.',
    arrivedRawDate: '2022-02-01',
    returnedDate: '04/02/2022',
    trackingNo: 'Trdt',
    status: 'returned',
    unitNo: '1086/01',
    recipientName: 'wilai pan',
    carrier: 'J&T Express',
    parcelType: 'กล่องขนาดใหญ่',
    hasImage: true,
    returnReason: 'ลูกค้าไม่มารับ เกิน 7 วัน',
    issuer: 'Silverman support',
  },
];

const AVAILABLE_UNITS = [
  { unitNo: '1086/01', residents: ['adisorn kaeophakdee', 'ดั๊ก ดำดี้', 'wilai pan'] },
  { unitNo: '1086/02', residents: ['พัชราภา', 'ttt'] },
  { unitNo: '1086/04', residents: ['วรัญญา'] },
  { unitNo: '1086/08', residents: ['Rose', 'คุณธนกฤต เลิศพิบูลย์'] },
  { unitNo: '1086/12', residents: ['adisorn kaeophakdee', 'kkk'] },
  { unitNo: '1086/14', residents: ['คุณวราภรณ์ สุวรรณเวช'] },
  { unitNo: '1086/20', residents: ['นะ ศรีสุระ', 'naretapple1 na', 'tobb tobsa'] },
  { unitNo: '1086/22', residents: ['คุณกิตติศักดิ์ ชัยชนะ'] },
  { unitNo: '1/3', residents: ['สวยใส แซ่บซ่า', 'กสิ กร'] },
  { unitNo: '11/12', residents: ['3fsf fsff'] },
  { unitNo: '22/22', residents: ['meaw meawmeaw'] },
];

const CARRIER_OPTIONS = [
  'ไปรษณีย์ไทย',
  'Flash Express',
  'J&T Express',
  'Kerry Express',
  'Ninja Van',
  'DHL',
  'Shopee',
  'Lazada Express',
  'Grab Express',
  'อื่นๆ',
];

const PARCEL_TYPE_OPTIONS = [
  'ซองจดหมาย/เอกสาร',
  'ซองขนาดเล็ก',
  'ซองขนาดใหญ่',
  'ห่อเล็ก',
  'ห่อกลาง',
  'ห่อใหญ่',
  'กล่องขนาดเล็ก',
  'กล่องขนาดกลาง',
  'กล่องขนาดใหญ่',
  'พัสดุควบคุมอุณหภูมิ (เย็น)',
];

interface Props {
  onBackToMenu: () => void;
  onNavigateSubView?: (view: string) => void;
}

export const AdminMailParcelManagementView: React.FC<Props> = ({
  onBackToMenu,
  onNavigateSubView,
}) => {
  // Master state
  const [parcels, setParcels] = useState<ParcelRecord[]>(INITIAL_PARCELS);

  // Active view: 'list' | 'create' | 'excel_report' | 'print_cards'
  const [viewMode, setViewMode] = useState<'list' | 'create' | 'excel_report' | 'print_cards'>('list');

  // Tabs: 'all' | 'uncollected' | 'collected' | 'deleted' | 'returned'
  const [activeTab, setActiveTab] = useState<
    'all' | 'uncollected' | 'collected' | 'deleted' | 'returned'
  >('all');

  // Filters & Search
  const [startDate, setStartDate] = useState('2020-01-01');
  const [endDate, setEndDate] = useState('2024-03-29');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [batchMenuOpen, setBatchMenuOpen] = useState(false);

  // Top Nav Submenu for "พิมพ์ใบรับพัสดุ ▾"
  const [printSlipMenuOpen, setPrintSlipMenuOpen] = useState(false);
  const [printFilterMode, setPrintFilterMode] = useState<'all' | 'no_app'>('all');

  // Row Action Menu (3 dots)
  const [actionMenuParcelId, setActionMenuParcelId] = useState<string | null>(null);

  // Modal 1: Receiver Details Modal (✓ มารับแล้ว)
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [targetCollectParcel, setTargetCollectParcel] = useState<ParcelRecord | null>(null);
  const [collectReceiverName, setCollectReceiverName] = useState('');
  const [collectDateTime, setCollectDateTime] = useState('2024-03-29T12:57');

  // Modal 2: Delete Confirm Warning (🗑️ ลบ)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [targetDeleteParcel, setTargetDeleteParcel] = useState<ParcelRecord | null>(null);

  // Modal 3: Reason Modal (สำหรับลบ หรือ ตีกลับ ↰)
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reasonActionType, setReasonActionType] = useState<'delete_reason' | 'return_reason'>('delete_reason');
  const [targetReasonParcel, setTargetReasonParcel] = useState<ParcelRecord | null>(null);
  const [reasonText, setReasonText] = useState('');

  // CREATE FORM STATE (Image 4 & 5)
  const [newRows, setNewRows] = useState<NewParcelRow[]>([
    {
      id: 'row-1',
      unitNo: '1/3',
      recipientName: 'สวยใส แซ่บซ่า',
      trackingNo: '',
      carrier: '',
      parcelType: '',
    },
  ]);

  // Filtered parcels based on Tab, Date range, and Search Query
  const filteredParcels = useMemo(() => {
    return parcels.filter((p) => {
      // Tab filter
      if (activeTab === 'uncollected' && p.status !== 'uncollected') return false;
      if (activeTab === 'collected' && p.status !== 'collected') return false;
      if (activeTab === 'deleted' && p.status !== 'deleted') return false;
      if (activeTab === 'returned' && p.status !== 'returned') return false;

      // Date Range Filter (compare arrivedRawDate or fallback)
      if (startDate && p.arrivedRawDate && p.arrivedRawDate < startDate) return false;
      if (endDate && p.arrivedRawDate && p.arrivedRawDate > endDate) return false;

      // Search Query Filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const match =
          p.parcelNo.toLowerCase().includes(q) ||
          p.unitNo.toLowerCase().includes(q) ||
          p.recipientName.toLowerCase().includes(q) ||
          p.trackingNo.toLowerCase().includes(q) ||
          p.carrier.toLowerCase().includes(q) ||
          (p.receiverName && p.receiverName.toLowerCase().includes(q)) ||
          p.parcelType.toLowerCase().includes(q);
        if (!match) return false;
      }

      return true;
    });
  }, [parcels, activeTab, startDate, endDate, searchQuery]);

  // Select all checkbox
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(filteredParcels.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // HANDLER: Add new row in Create Form
  const handleAddCreateRow = () => {
    const nextRowNumber = newRows.length + 1;
    setNewRows((prev) => [
      ...prev,
      {
        id: `row-${Date.now()}-${nextRowNumber}`,
        unitNo: '',
        recipientName: '',
        trackingNo: '',
        carrier: '',
        parcelType: '',
      },
    ]);
  };

  const handleRemoveCreateRow = (id: string) => {
    if (newRows.length <= 1) return;
    setNewRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleUpdateCreateRow = (id: string, field: keyof NewParcelRow, value: string) => {
    setNewRows((prev) =>
      prev.map((r) => {
        if (r.id === id) {
          const updated = { ...r, [field]: value };
          // If unit changed, autofill recipient name if empty
          if (field === 'unitNo') {
            const matchedUnit = AVAILABLE_UNITS.find((u) => u.unitNo === value);
            if (matchedUnit && matchedUnit.residents.length > 0) {
              updated.recipientName = matchedUnit.residents[0];
            }
          }
          return updated;
        }
        return r;
      })
    );
  };

  // HANDLER: Save created parcels
  const handleSaveCreateParcels = () => {
    const validRows = newRows.filter((r) => r.unitNo.trim() !== '');
    if (validRows.length === 0) {
      alert('กรุณาเลือกยูนิตสำหรับรายการพัสดุอย่างน้อย 1 รายการ');
      return;
    }

    const now = new Date();
    const formattedDate = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${now.getFullYear() + 543} - ${now
      .getHours()
      .toString()
      .padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')} น.`;
    const yyyymmdd = now.toISOString().split('T')[0];

    const newCreated: ParcelRecord[] = validRows.map((r, idx) => {
      const pNum = `${now.getFullYear().toString().slice(2)}${(now.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${(parcels.length + idx + 1).toString().padStart(2, '0')}`;
      return {
        id: `p-${Date.now()}-${idx}`,
        parcelNo: pNum,
        arrivedDate: formattedDate,
        arrivedRawDate: yyyymmdd,
        trackingNo: r.trackingNo || '123123',
        status: 'uncollected',
        unitNo: r.unitNo,
        recipientName: r.recipientName || 'เจ้าของร่วม',
        receiverName: '-',
        carrier: r.carrier || 'ไปรษณีย์ไทย',
        parcelType: r.parcelType || 'ซองจดหมาย/เอกสาร',
        hasImage: !!r.imageUrl,
        imageUrl: r.imageUrl,
        issuer: 'Silverman support',
        reminderRound: '-',
      };
    });

    setParcels((prev) => [...newCreated, ...prev]);
    setViewMode('list');
    setActiveTab('uncollected');
  };

  // HANDLER: Click "✓ มารับแล้ว" -> Open Modal
  const handleOpenCollectModal = (parcel: ParcelRecord) => {
    setTargetCollectParcel(parcel);
    setCollectReceiverName(parcel.recipientName || 'เทส');
    const now = new Date();
    setCollectDateTime(now.toISOString().slice(0, 16));
    setShowCollectModal(true);
    setActionMenuParcelId(null);
  };

  // Confirm Collected
  const handleConfirmCollect = () => {
    if (!targetCollectParcel) return;
    const dateObj = new Date(collectDateTime);
    const dateFormatted = `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${dateObj.getFullYear() + 543} - ${dateObj
      .getHours()
      .toString()
      .padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')} น.`;

    setParcels((prev) =>
      prev.map((p) =>
        p.id === targetCollectParcel.id
          ? {
              ...p,
              status: 'collected',
              collectedDate: dateFormatted,
              receiverName: `${collectReceiverName || p.recipientName} (Silverman support)`,
            }
          : p
      )
    );
    setShowCollectModal(false);
    setTargetCollectParcel(null);
  };

  // HANDLER: Click "↰ ตีกลับ" -> Open Reason Modal
  const handleOpenReturnModal = (parcel: ParcelRecord) => {
    setTargetReasonParcel(parcel);
    setReasonActionType('return_reason');
    setReasonText('');
    setShowReasonModal(true);
    setActionMenuParcelId(null);
  };

  // HANDLER: Click "🗑️ ลบ" -> Open Warning Modal
  const handleOpenDeleteConfirm = (parcel: ParcelRecord) => {
    setTargetDeleteParcel(parcel);
    setShowDeleteConfirmModal(true);
    setActionMenuParcelId(null);
  };

  // If user clicks "ใช่" on delete warning -> open reason modal for deletion
  const handleConfirmDeleteStep1 = () => {
    if (!targetDeleteParcel) return;
    setShowDeleteConfirmModal(false);
    setTargetReasonParcel(targetDeleteParcel);
    setReasonActionType('delete_reason');
    setReasonText('เทส');
    setShowReasonModal(true);
  };

  // Confirm Reason Submission (For Delete or Return)
  const handleConfirmReason = () => {
    if (!targetReasonParcel) return;
    const now = new Date();
    const todayFormatted = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${now.getFullYear() + 543}`;

    if (reasonActionType === 'delete_reason') {
      setParcels((prev) =>
        prev.map((p) =>
          p.id === targetReasonParcel.id
            ? {
                ...p,
                status: 'deleted',
                deletedDate: todayFormatted,
                deletedBy: 'Silverman support',
                deleteReason: reasonText || 'ลบ',
              }
            : p
        )
      );
    } else {
      setParcels((prev) =>
        prev.map((p) =>
          p.id === targetReasonParcel.id
            ? {
                ...p,
                status: 'returned',
                returnedDate: todayFormatted,
                returnReason: reasonText || 'ลูกค้าไม่มารับ เกิน 7 วัน',
              }
            : p
        )
      );
    }

    setShowReasonModal(false);
    setTargetReasonParcel(null);
    setTargetDeleteParcel(null);
  };

  // Top Nav Submenu for Printing Parcel Slips
  const handleOpenPrintCards = (filter: 'all' | 'no_app') => {
    setPrintFilterMode(filter);
    setPrintSlipMenuOpen(false);
    setViewMode('print_cards');
  };

  // Helper for status label text
  const getStatusDisplay = (status: ParcelStatus) => {
    switch (status) {
      case 'uncollected':
        return 'ยังไม่มารับ';
      case 'collected':
        return 'มารับเรียบร้อยแล้ว';
      case 'deleted':
        return 'รายการที่ลบแล้ว';
      case 'returned':
        return 'รายการตีกลับ';
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs min-h-[85vh] flex flex-col font-sans text-slate-800">
      {/* ========================================================================= */}
      {/* 1. PRINT CARDS VIEW (Image 17 - ใบแจ้งรับพัสดุ / Parcel Card) */}
      {/* ========================================================================= */}
      {viewMode === 'print_cards' && (
        <div className="p-6 bg-slate-100 min-h-screen flex flex-col items-center space-y-6">
          {/* Top Bar with Print & Back */}
          <div className="w-full max-w-4xl flex items-center justify-between no-print">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 cursor-pointer shadow-2xs"
            >
              <ChevronLeft className="w-4 h-4" /> กลับสู่รายการพัสดุ
            </button>

            {/* Blue PRINT Button */}
            <button
              type="button"
              onClick={() => window.print()}
              className="px-8 py-2 bg-blue-900 hover:bg-blue-950 text-white rounded-md text-xs font-bold shadow-md cursor-pointer tracking-wider"
            >
              PRINT
            </button>
          </div>

          {/* Printable 2x2 Grid Sheets matching Image 17 */}
          <div className="w-full max-w-4xl bg-white border border-slate-300 p-8 rounded-sm shadow-xl space-y-8 print:m-0 print:p-4 print:border-none print:shadow-none min-h-[1050px]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:grid-cols-2">
              {(selectedIds.length > 0
                ? parcels.filter((p) => selectedIds.includes(p.id))
                : parcels.slice(0, 4)
              ).map((cardItem) => (
                <div
                  key={cardItem.id}
                  className="border-2 border-dashed border-slate-400 p-6 rounded-sm space-y-4 font-sans bg-white relative text-slate-900"
                >
                  {/* Card Header & Silverman Logo */}
                  <div className="text-center space-y-1">
                    <div className="inline-flex flex-col items-center">
                      <span className="text-[10px] tracking-widest text-slate-500 font-bold uppercase">
                        Smart Property Solution
                      </span>
                      <h3 className="text-lg font-black tracking-tight text-slate-900">
                        SILVERMAN
                      </h3>
                    </div>
                    <h4 className="text-sm font-bold text-slate-800 pt-1">
                      ใบแจ้งรับพัสดุ / Parcel Card
                    </h4>
                  </div>

                  {/* Parcel Details 2 Columns */}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11px] pt-2 leading-tight">
                    <div>
                      <span className="font-bold block">ห้อง : {cardItem.unitNo}</span>
                      <span className="text-slate-500 text-[10px]">Room No.</span>
                    </div>
                    <div>
                      <span className="font-bold block">ลำดับพัสดุ {cardItem.parcelNo}</span>
                      <span className="text-slate-500 text-[10px]">Parcel No.</span>
                    </div>

                    <div>
                      <span className="font-bold block">วันที่มาส่ง {cardItem.arrivedDate.split(' - ')[0]}</span>
                      <span className="text-slate-500 text-[10px]">Parcel Date</span>
                    </div>
                    <div>
                      <span className="font-bold block">ชื่อผู้รับ {cardItem.recipientName}</span>
                      <span className="text-slate-500 text-[10px]">Receiver</span>
                    </div>

                    <div>
                      <span className="font-bold block font-mono">รหัสพัสดุ {cardItem.trackingNo}</span>
                      <span className="text-slate-500 text-[10px]">Tracking No.</span>
                    </div>
                    <div>
                      <span className="font-bold block">ลักษณะพัสดุ {cardItem.parcelType}</span>
                      <span className="text-slate-500 text-[10px]">Description Letter</span>
                    </div>

                    <div>
                      <span className="font-bold block">จัดส่งโดย {cardItem.carrier}</span>
                      <span className="text-slate-500 text-[10px]">Delivery</span>
                    </div>
                    <div className="flex flex-col items-center justify-end">
                      <div className="w-full border border-slate-400 h-9 bg-slate-50/50"></div>
                      <span className="text-[10px] text-slate-600 font-medium mt-0.5">
                        ลายเซ็นผู้รับ / Signature
                      </span>
                    </div>
                  </div>

                  {/* QR code and App promo section matching Image 17 */}
                  <div className="border-t border-dashed border-slate-300 pt-3 text-center space-y-2">
                    <span className="text-[11px] font-bold text-slate-700 block">
                      ดาวน์โหลด Application ได้ที่
                    </span>
                    <div className="inline-flex items-center justify-center border border-slate-300 rounded-full px-4 py-0.5 text-[10px] text-slate-600 bg-slate-50 gap-1">
                      <Search className="w-3 h-3 text-slate-400" /> Silverman
                    </div>

                    {/* QR Code Graphic */}
                    <div className="flex justify-center pt-1">
                      <div className="w-16 h-16 border-2 border-slate-800 p-1 flex items-center justify-center">
                        <QrCode className="w-full h-full text-slate-900" />
                      </div>
                    </div>

                    <p className="text-[9px] text-slate-500 pt-1">
                      กรุณานำใบนี้มาติดต่อเพื่อรับพัสดุ / Please bring this card to claim your parcel.
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 3. EXCEL SPREADSHEET REPORT VIEW (Image 15 - รายงานพัสดุ) */}
      {/* ========================================================================= */}
      {viewMode === 'excel_report' && (
        <div className="p-6 space-y-4 bg-slate-50 min-h-screen">
          {/* Top Actions */}
          <div className="flex items-center justify-between no-print">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 text-xs font-semibold text-slate-700 cursor-pointer shadow-2xs"
            >
              <ChevronLeft className="w-4 h-4" /> กลับ
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.print()}
                className="px-4 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-xs cursor-pointer"
              >
                <Printer className="w-3.5 h-3.5" /> สั่งพิมพ์ / ส่งออก
              </button>
            </div>
          </div>

          {/* Spreadsheet Table matching Image 15 */}
          <div className="bg-white border border-slate-300 rounded shadow-md overflow-x-auto text-[11px] font-sans">
            {/* Header Titles */}
            <div className="p-4 border-b border-slate-200 space-y-1 bg-slate-50/50">
              <h2 className="text-sm font-bold text-slate-900">
                นิติบุคคลหมู่บ้านจัดสรร เอส วี ซัพพอร์ต
              </h2>
              <h3 className="text-xs font-semibold text-slate-800">รายงานพัสดุ</h3>
              <p className="text-[11px] text-slate-600">เรียงตามสถานะ - ทั้งหมด</p>
              <p className="text-[11px] text-slate-600">
                เรียงตามวันที่ {startDate} ถึง {endDate}
              </p>
            </div>

            {/* Grid Table */}
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-100 border-y border-slate-300 text-slate-700 font-bold">
                  <th className="p-2 border-r border-slate-300 w-12 text-center">ลำดับ</th>
                  <th className="p-2 border-r border-slate-300 w-36">วันที่มาส่ง</th>
                  <th className="p-2 border-r border-slate-300 w-28">Tracking</th>
                  <th className="p-2 border-r border-slate-300 w-28">สถานะพัสดุ</th>
                  <th className="p-2 border-r border-slate-300 w-20">ห้อง</th>
                  <th className="p-2 border-r border-slate-300 w-36">ชื่อ-นามสกุล</th>
                  <th className="p-2 border-r border-slate-300 w-36">ชื่อผู้รับ</th>
                  <th className="p-2 border-r border-slate-300 w-28">จัดส่งโดย</th>
                  <th className="p-2">ลักษณะพัสดุ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-slate-800">
                {parcels.map((item, idx) => (
                  <tr key={item.id} className="hover:bg-slate-50 font-sans">
                    <td className="p-2 border-r border-slate-200 text-center font-mono">
                      {item.parcelNo}
                    </td>
                    <td className="p-2 border-r border-slate-200">{item.arrivedDate}</td>
                    <td className="p-2 border-r border-slate-200 font-mono">{item.trackingNo}</td>
                    <td className="p-2 border-r border-slate-200 font-medium">
                      {getStatusDisplay(item.status)}
                    </td>
                    <td className="p-2 border-r border-slate-200 font-mono font-bold">
                      {item.unitNo}
                    </td>
                    <td className="p-2 border-r border-slate-200">{item.recipientName}</td>
                    <td className="p-2 border-r border-slate-200">{item.receiverName || '-'}</td>
                    <td className="p-2 border-r border-slate-200">{item.carrier}</td>
                    <td className="p-2">{item.parcelType}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Bottom Tab Bar matching Image 15 */}
            <div className="bg-slate-100 border-t border-slate-300 p-2 flex items-center gap-2">
              <div className="px-3 py-1 bg-white border border-slate-300 rounded-t text-xs font-bold text-emerald-700 shadow-2xs">
                parcel
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 4. CREATE PARCEL SCREEN (Image 4 & 5 - สร้างรายการพัสดุ) */}
      {/* ========================================================================= */}
      {viewMode === 'create' && (
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className="w-8 h-8 rounded-full border border-slate-300 hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold text-slate-900">สร้างรายการพัสดุ</h1>
            </div>

            {/* Action Buttons matching Image 4 */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className="px-5 py-2 rounded-lg bg-slate-300 hover:bg-slate-400 text-slate-800 text-xs font-bold transition-colors cursor-pointer"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleSaveCreateParcels}
                className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-xs transition-colors cursor-pointer"
              >
                ยืนยันและส่ง
              </button>
            </div>
          </div>

          {/* Form Table matching Image 4 & 5 */}
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs bg-white">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-100/90 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="p-3 w-12 text-center"></th>
                  <th className="p-3 w-40">ยูนิต</th>
                  <th className="p-3 w-48">ชื่อผู้รับ</th>
                  <th className="p-3 w-56">Tracking</th>
                  <th className="p-3 w-44">จัดส่งโดย</th>
                  <th className="p-3 w-48">ลักษณะพัสดุ</th>
                  <th className="p-3 w-24 text-center">รูปภาพ</th>
                  <th className="p-3 w-12 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {newRows.map((row, index) => {
                  const unitData = AVAILABLE_UNITS.find((u) => u.unitNo === row.unitNo);
                  return (
                    <tr key={row.id} className="hover:bg-slate-50/50">
                      <td className="p-3 text-center font-bold text-slate-600">
                        {index + 1}.
                      </td>

                      {/* ยูนิต Dropdown */}
                      <td className="p-3">
                        <select
                          value={row.unitNo}
                          onChange={(e) =>
                            handleUpdateCreateRow(row.id, 'unitNo', e.target.value)
                          }
                          className="w-full p-2 border border-slate-300 rounded-lg text-xs bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">เลือกยูนิต...</option>
                          {AVAILABLE_UNITS.map((u) => (
                            <option key={u.unitNo} value={u.unitNo}>
                              {u.unitNo}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* ชื่อผู้รับ Dropdown */}
                      <td className="p-3">
                        <select
                          value={row.recipientName}
                          onChange={(e) =>
                            handleUpdateCreateRow(row.id, 'recipientName', e.target.value)
                          }
                          className="w-full p-2 border border-slate-300 rounded-lg text-xs bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select...</option>
                          {unitData ? (
                            unitData.residents.map((res) => (
                              <option key={res} value={res}>
                                {res}
                              </option>
                            ))
                          ) : (
                            <option value="เจ้าของร่วม">เจ้าของร่วม</option>
                          )}
                        </select>
                      </td>

                      {/* Tracking Input */}
                      <td className="p-3">
                        <input
                          type="text"
                          placeholder="กรอกหมายเลข Tracking"
                          value={row.trackingNo}
                          onChange={(e) =>
                            handleUpdateCreateRow(row.id, 'trackingNo', e.target.value)
                          }
                          className="w-full p-2 border border-slate-300 rounded-lg text-xs bg-white text-slate-800 focus:outline-none font-mono"
                        />
                      </td>

                      {/* จัดส่งโดย Dropdown */}
                      <td className="p-3">
                        <select
                          value={row.carrier}
                          onChange={(e) =>
                            handleUpdateCreateRow(row.id, 'carrier', e.target.value)
                          }
                          className="w-full p-2 border border-slate-300 rounded-lg text-xs bg-white text-slate-800 focus:outline-none"
                        >
                          <option value="">กรุณาเลือก</option>
                          {CARRIER_OPTIONS.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* ลักษณะพัสดุ Dropdown */}
                      <td className="p-3">
                        <select
                          value={row.parcelType}
                          onChange={(e) =>
                            handleUpdateCreateRow(row.id, 'parcelType', e.target.value)
                          }
                          className="w-full p-2 border border-slate-300 rounded-lg text-xs bg-white text-slate-800 focus:outline-none"
                        >
                          <option value="">กรุณาเลือก</option>
                          {PARCEL_TYPE_OPTIONS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* รูปภาพ Upload button [+] */}
                      <td className="p-3 text-center">
                        <label className="w-8 h-8 rounded-lg border border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50 flex items-center justify-center mx-auto cursor-pointer transition-colors text-slate-500 hover:text-blue-600">
                          <Plus className="w-4 h-4" />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const url = URL.createObjectURL(file);
                                handleUpdateCreateRow(row.id, 'imageUrl', url);
                              }
                            }}
                          />
                        </label>
                      </td>

                      {/* Delete row */}
                      <td className="p-3 text-center">
                        {newRows.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveCreateRow(row.id)}
                            className="p-1 text-slate-400 hover:text-rose-600 transition-colors cursor-pointer"
                            title="ลบแถวนี้"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* "+ เพิ่มรายการ" Button matching Image 4 & 5 */}
          <div>
            <button
              type="button"
              onClick={handleAddCreateRow}
              className="px-5 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> เพิ่มรายการ
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 5. MAIN LIST VIEW (Images 2, 3, 6, 9, 10, 13, 14, 16) */}
      {/* ========================================================================= */}
      {viewMode === 'list' && (
        <div className="p-5 sm:p-6 space-y-4">
          {/* Top Title & Action Buttons matching Image 2 & 3 */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={onBackToMenu}
                className="w-8 h-8 rounded-full border border-slate-300 hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors cursor-pointer"
                title="กลับสู่เมนูหลัก"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold text-slate-900">รายการจดหมาย พัสดุ</h1>
            </div>

            {/* Right Buttons matching Image 2 & 3 */}
            <div className="flex items-center gap-2.5 self-start sm:self-auto">
              {/* 1. พิมพ์ใบรับพัสดุ ▾ */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPrintSlipMenuOpen(!printSlipMenuOpen)}
                  className="px-4 py-2 rounded-lg border border-sky-600 text-sky-600 hover:bg-sky-50 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
                >
                  <span>พิมพ์ใบรับพัสดุ</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>

                {/* Dropdown menu matching Image 16 */}
                {printSlipMenuOpen && (
                  <div className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-lg p-1.5 z-30 space-y-1 text-xs animate-in fade-in slide-in-from-top-1">
                    <button
                      type="button"
                      onClick={() => handleOpenPrintCards('all')}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 font-medium text-slate-700 flex items-center gap-2 cursor-pointer"
                    >
                      <Check className="w-3.5 h-3.5 text-blue-600" /> ทั้งหมด
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOpenPrintCards('no_app')}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 font-medium text-slate-700 flex items-center gap-2 cursor-pointer"
                    >
                      <Check className="w-3.5 h-3.5 text-blue-600" /> ยังไม่ใช้แอปพลิเคชั่น
                    </button>
                  </div>
                )}
              </div>

              {/* 2. 🖨️ พิมพ์ (Export Excel View) */}
              <button
                type="button"
                onClick={() => setViewMode('excel_report')}
                className="px-4 py-2 rounded-lg bg-slate-300 hover:bg-slate-400 text-slate-800 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
              >
                <Printer className="w-3.5 h-3.5" /> พิมพ์
              </button>

              {/* 3. + สร้างรายการพัสดุ */}
              <button
                type="button"
                onClick={() => {
                  setNewRows([
                    {
                      id: `row-${Date.now()}-1`,
                      unitNo: '1/3',
                      recipientName: 'สวยใส แซ่บซ่า',
                      trackingNo: '',
                      carrier: '',
                      parcelType: '',
                    },
                    {
                      id: `row-${Date.now()}-2`,
                      unitNo: '1/3',
                      recipientName: '',
                      trackingNo: '',
                      carrier: '',
                      parcelType: '',
                    },
                    {
                      id: `row-${Date.now()}-3`,
                      unitNo: '',
                      recipientName: '',
                      trackingNo: '',
                      carrier: '',
                      parcelType: '',
                    },
                  ]);
                  setViewMode('create');
                }}
                className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4" /> สร้างรายการพัสดุ
              </button>
            </div>
          </div>

          {/* Tabs matching Images: ทั้งหมด | ยังไม่มารับ | มารับเรียบร้อยแล้ว | รายการที่ลบแล้ว | รายการตีกลับ */}
          <div className="flex items-center gap-6 border-b border-slate-200 text-xs font-semibold text-slate-600 overflow-x-auto no-scrollbar pt-1">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`pb-2.5 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'all'
                  ? 'text-blue-600 font-bold border-b-2 border-blue-600'
                  : 'hover:text-slate-900'
              }`}
            >
              ทั้งหมด
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('uncollected')}
              className={`pb-2.5 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'uncollected'
                  ? 'text-blue-600 font-bold border-b-2 border-blue-600'
                  : 'hover:text-slate-900'
              }`}
            >
              ยังไม่มารับ
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('collected')}
              className={`pb-2.5 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'collected'
                  ? 'text-blue-600 font-bold border-b-2 border-blue-600'
                  : 'hover:text-slate-900'
              }`}
            >
              มารับเรียบร้อยแล้ว
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('deleted')}
              className={`pb-2.5 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'deleted'
                  ? 'text-blue-600 font-bold border-b-2 border-blue-600'
                  : 'hover:text-slate-900'
              }`}
            >
              รายการที่ลบแล้ว
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('returned')}
              className={`pb-2.5 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'returned'
                  ? 'text-blue-600 font-bold border-b-2 border-blue-600'
                  : 'hover:text-slate-900'
              }`}
            >
              รายการตีกลับ
            </button>
          </div>

          {/* Filter Bar: จัดการหลายรายการ ▾, เรียงดูตามวัน, Search */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pt-1">
            <div className="flex items-center gap-2">
              {/* จัดการหลายรายการ ▾ */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setBatchMenuOpen(!batchMenuOpen)}
                  className="px-3.5 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 flex items-center gap-2 cursor-pointer shadow-2xs"
                >
                  <span>จัดการหลายรายการ</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                </button>

                {batchMenuOpen && (
                  <div className="absolute left-0 mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-lg p-1.5 z-20 space-y-1 text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedIds.length === 0) return;
                        handleOpenPrintCards('all');
                        setBatchMenuOpen(false);
                      }}
                      disabled={selectedIds.length === 0}
                      className={`w-full text-left px-3 py-1.5 rounded-lg font-medium flex items-center gap-2 ${
                        selectedIds.length > 0
                          ? 'text-slate-700 hover:bg-slate-50 cursor-pointer'
                          : 'text-slate-300 cursor-not-allowed'
                      }`}
                    >
                      <Printer className="w-3.5 h-3.5" /> พิมพ์ใบรับพัสดุที่เลือก ({selectedIds.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedIds.length === 0) return;
                        setParcels((prev) =>
                          prev.map((p) =>
                            selectedIds.includes(p.id)
                              ? {
                                  ...p,
                                  status: 'collected',
                                  collectedDate: '29/03/2024 - 12:57 น.',
                                  receiverName: 'เจ้าของร่วม (Silverman support)',
                                }
                              : p
                          )
                        );
                        setSelectedIds([]);
                        setBatchMenuOpen(false);
                      }}
                      disabled={selectedIds.length === 0}
                      className={`w-full text-left px-3 py-1.5 rounded-lg font-medium flex items-center gap-2 ${
                        selectedIds.length > 0
                          ? 'text-emerald-700 hover:bg-emerald-50 cursor-pointer'
                          : 'text-slate-300 cursor-not-allowed'
                      }`}
                    >
                      <Check className="w-3.5 h-3.5" /> ทำเครื่องหมายมารับแล้ว
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Date Range & Search matching Images */}
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="flex items-center gap-1.5 text-xs text-slate-700">
                <span className="font-medium shrink-0">เรียงดูตามวัน</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-2 py-1 border border-slate-300 rounded-lg text-xs bg-white text-slate-700 focus:outline-none"
                />
                <span>ถึง</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-2 py-1 border border-slate-300 rounded-lg text-xs bg-white text-slate-700 focus:outline-none"
                />
              </div>

              {/* Search Box + Blue ค้นหา Button */}
              <div className="flex items-center">
                <input
                  type="text"
                  placeholder="ค้นหา"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-40 sm:w-48 px-3 py-1 border border-slate-300 rounded-l-lg text-xs bg-white text-slate-700 focus:outline-none"
                />
                <button
                  type="button"
                  className="px-4 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-r-lg cursor-pointer"
                >
                  ค้นหา
                </button>
              </div>
            </div>
          </div>

          {/* ========================================================================= */}
          {/* DATA TABLE (Adaptive columns per tab matching images) */}
          {/* ========================================================================= */}
          <div className="border border-slate-200 rounded-xl overflow-x-auto shadow-2xs bg-white">
            <table className="w-full text-left text-xs min-w-[900px]">
              <thead className="bg-slate-100/90 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="p-3 w-10 text-center">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={
                        filteredParcels.length > 0 &&
                        selectedIds.length === filteredParcels.length
                      }
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="p-3 w-24">ลำดับ</th>

                  {/* Dynamic Date Header */}
                  {activeTab === 'collected' && <th className="p-3 w-36">วันที่มารับ ▾</th>}
                  {activeTab === 'deleted' && <th className="p-3 w-28">วันที่ลบ ▾</th>}
                  {activeTab === 'returned' && <th className="p-3 w-28">วันที่ตีกลับ ▾</th>}

                  <th className="p-3 w-36">วันที่มาส่ง ▴</th>
                  <th className="p-3 w-28">Tracking</th>

                  {/* If All Tab, show Status column */}
                  {activeTab === 'all' && <th className="p-3 w-28">สถานะพัสดุ</th>}

                  <th className="p-3 w-20">ห้อง</th>
                  <th className="p-3 w-36">ชื่อ-นามสกุล</th>

                  {/* If collected or all tab, show receiver */}
                  {(activeTab === 'all' || activeTab === 'collected') && (
                    <th className="p-3 w-36">ชื่อผู้รับ</th>
                  )}

                  <th className="p-3 w-28">จัดส่งโดย</th>
                  <th className="p-3 w-32">ลักษณะพัสดุ</th>

                  {/* Extra tab-specific columns */}
                  {activeTab === 'uncollected' && (
                    <>
                      <th className="p-3 w-32">ออกโดย</th>
                      <th className="p-3 w-36">แจ้งเตือนซ้ำ</th>
                    </>
                  )}

                  {activeTab === 'deleted' && (
                    <>
                      <th className="p-3 w-36">ผู้ลบรายการ</th>
                      <th className="p-3 w-28">สาเหตุการลบ</th>
                    </>
                  )}

                  {activeTab === 'returned' && (
                    <th className="p-3 w-40">หมายเหตุตีกลับ</th>
                  )}

                  {/* Row Actions column */}
                  <th className="p-3 w-12 text-center"></th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredParcels.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="py-12 text-center text-slate-400">
                      ไม่พบข้อมูลรายการพัสดุในเงื่อนไขที่เลือก
                    </td>
                  </tr>
                ) : (
                  filteredParcels.map((parcel) => {
                    const isSelected = selectedIds.includes(parcel.id);
                    return (
                      <tr
                        key={parcel.id}
                        className={`hover:bg-slate-50/80 group transition-colors ${
                          isSelected ? 'bg-blue-50/30' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="p-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(parcel.id)}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                        </td>

                        {/* ลำดับ */}
                        <td className="p-3 font-mono text-slate-700">{parcel.parcelNo}</td>

                        {/* Specific date columns */}
                        {activeTab === 'collected' && (
                          <td className="p-3 font-mono text-slate-800 font-medium">
                            {parcel.collectedDate || '-'}
                          </td>
                        )}
                        {activeTab === 'deleted' && (
                          <td className="p-3 font-mono text-slate-700">
                            {parcel.deletedDate || '-'}
                          </td>
                        )}
                        {activeTab === 'returned' && (
                          <td className="p-3 font-mono text-slate-700">
                            {parcel.returnedDate || '-'}
                          </td>
                        )}

                        {/* วันที่มาส่ง */}
                        <td className="p-3 font-mono text-slate-700">{parcel.arrivedDate}</td>

                        {/* Tracking */}
                        <td className="p-3 font-mono text-slate-800">{parcel.trackingNo}</td>

                        {/* สถานะ (Tab All) */}
                        {activeTab === 'all' && (
                          <td className="p-3">
                            <span
                              className={`font-semibold ${
                                parcel.status === 'uncollected'
                                  ? 'text-amber-600'
                                  : parcel.status === 'collected'
                                  ? 'text-emerald-600'
                                  : parcel.status === 'deleted'
                                  ? 'text-slate-500'
                                  : 'text-rose-600'
                              }`}
                            >
                              {getStatusDisplay(parcel.status)}
                            </span>
                          </td>
                        )}

                        {/* ห้อง */}
                        <td className="p-3 font-mono font-medium text-slate-900">
                          {parcel.unitNo}
                        </td>

                        {/* ชื่อ-นามสกุล */}
                        <td className="p-3 text-slate-800">{parcel.recipientName}</td>

                        {/* ชื่อผู้รับ */}
                        {(activeTab === 'all' || activeTab === 'collected') && (
                          <td className="p-3 text-slate-600">{parcel.receiverName || '-'}</td>
                        )}

                        {/* จัดส่งโดย */}
                        <td className="p-3 text-slate-700">{parcel.carrier}</td>

                        {/* ลักษณะพัสดุ */}
                        <td className="p-3">
                          <div className="flex items-center gap-1.5">
                            {parcel.hasImage && (
                              <div className="w-5 h-5 rounded bg-amber-100 flex items-center justify-center shrink-0">
                                <Box className="w-3 h-3 text-amber-600" />
                              </div>
                            )}
                            <span>{parcel.parcelType}</span>
                          </div>
                        </td>

                        {/* Uncollected specific columns */}
                        {activeTab === 'uncollected' && (
                          <>
                            <td className="p-3 text-slate-600">{parcel.issuer}</td>
                            <td className="p-3 text-slate-600 font-mono text-[11px]">
                              {parcel.reminderRound || '-'}
                            </td>
                          </>
                        )}

                        {/* Deleted specific columns */}
                        {activeTab === 'deleted' && (
                          <>
                            <td className="p-3 text-slate-600">{parcel.deletedBy || '-'}</td>
                            <td className="p-3 text-slate-700">{parcel.deleteReason || '-'}</td>
                          </>
                        )}

                        {/* Returned specific columns */}
                        {activeTab === 'returned' && (
                          <td className="p-3 text-rose-600 font-medium">
                            {parcel.returnReason || '-'}
                          </td>
                        )}

                        {/* Row Action 3-dots Menu matching Image 6 & 10 */}
                        <td className="p-3 text-center relative">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActionMenuParcelId(
                                actionMenuParcelId === parcel.id ? null : parcel.id
                              );
                            }}
                            className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          {/* Popover Action Menu */}
                          {actionMenuParcelId === parcel.id && (
                            <div className="absolute right-3 top-8 w-36 bg-white border border-slate-200 rounded-xl shadow-xl p-1.5 z-40 space-y-1 text-xs text-left animate-in fade-in zoom-in-95">
                              {parcel.status !== 'collected' && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenCollectModal(parcel)}
                                  className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-50 text-slate-700 flex items-center gap-2 cursor-pointer"
                                >
                                  <Check className="w-3.5 h-3.5 text-emerald-600" /> มารับแล้ว
                                </button>
                              )}

                              {parcel.status !== 'returned' && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenReturnModal(parcel)}
                                  className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-50 text-slate-700 flex items-center gap-2 cursor-pointer"
                                >
                                  <RotateCcw className="w-3.5 h-3.5 text-blue-600" /> ตีกลับ
                                </button>
                              )}

                              {parcel.status !== 'deleted' && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenDeleteConfirm(parcel)}
                                  className="w-full text-left px-2.5 py-1.5 rounded hover:bg-rose-50 text-rose-600 flex items-center gap-2 cursor-pointer font-medium"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> ลบ
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination bar matching Image 9 */}
          <div className="flex items-center justify-between text-xs text-slate-500 pt-2">
            <div className="flex items-center gap-2">
              <span>แสดง 10 รายการ/หน้า ▾</span>
            </div>
            <span>รายการทั้งหมด {filteredParcels.length}</span>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: ข้อมูลผู้รับพัสดุ (Image 7 & 8) */}
      {/* ========================================================================= */}
      {showCollectModal && targetCollectParcel && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full p-6 space-y-5 animate-in zoom-in-95">
            <h3 className="text-lg font-bold text-slate-900 text-center">ข้อมูลผู้รับพัสดุ</h3>

            <div className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="font-semibold text-slate-800 block">ชื่อผู้รับ</label>
                <input
                  type="text"
                  value={collectReceiverName}
                  onChange={(e) => setCollectReceiverName(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-semibold text-slate-800 block">วันเวลาที่มารับ</label>
                <input
                  type="datetime-local"
                  value={collectDateTime}
                  onChange={(e) => setCollectDateTime(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Buttons matching Image 7 */}
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowCollectModal(false)}
                className="px-6 py-2 rounded-lg bg-slate-500 hover:bg-slate-600 text-white text-xs font-bold cursor-pointer transition-colors"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmCollect}
                className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold cursor-pointer shadow-xs transition-colors"
              >
                ยืนยัน
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: CONFIRM DELETE WARNING (Image 11) */}
      {/* ========================================================================= */}
      {showDeleteConfirmModal && targetDeleteParcel && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-sm w-full p-6 text-center space-y-4 animate-in zoom-in-95">
            {/* Orange Warning Circle Icon */}
            <div className="w-16 h-16 rounded-full border-2 border-amber-300 bg-amber-50 text-amber-500 flex items-center justify-center mx-auto text-3xl font-light">
              !
            </div>

            <div className="space-y-1">
              <h3 className="text-base font-bold text-slate-900">
                คุณแน่ใจที่จะลบรายการพัสดุ?
              </h3>
              <p className="text-xs text-slate-500">พัสดุทั้งหมด 1 รายการ</p>
            </div>

            {/* Action Buttons matching Image 11 */}
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirmModal(false)}
                className="px-6 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold cursor-pointer transition-colors"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteStep1}
                className="px-8 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold cursor-pointer shadow-xs transition-colors"
              >
                ใช่
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: หมายเหตุ (Image 12) */}
      {/* ========================================================================= */}
      {showReasonModal && targetReasonParcel && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full p-6 space-y-5 animate-in zoom-in-95">
            <h3 className="text-lg font-bold text-slate-900 text-center">หมายเหตุ</h3>

            <div className="space-y-2">
              <input
                type="text"
                placeholder={
                  reasonActionType === 'delete_reason'
                    ? 'ระบุสาเหตุการลบ (เช่น เทส, ลบรายการซ้ำ)'
                    : 'ระบุเหตุผลการตีกลับ (เช่น ชื่อผิด, ส่งผิด, ลูกค้าไม่มารับ)'
                }
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-xs bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Buttons matching Image 12 */}
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowReasonModal(false)}
                className="px-6 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold cursor-pointer transition-colors"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmReason}
                className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold cursor-pointer shadow-xs transition-colors"
              >
                ยืนยัน
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
